/**
 * The manage list: everything the user has saved, and finding one of it.
 *
 * Three ways to read the same list, because the user arrives with three different questions. By
 * **area** by default — "what have I got in Đà Lạt" is what most visits are for, and it is the shape a
 * trip is planned in. By **kind** for "where was that café". **Flat** for hunting one row by name, when a
 * heading between rows is only something to scroll past.
 *
 * The sections fold, and the biggest one opens by itself: a user with fourteen places in one area and one
 * in another should not scroll past a heading to reach what they came for, and a user with six areas gets
 * one tap to reach the rest. Folded sections still carry their count, so the closed list is informative
 * rather than a row of names.
 *
 * The search runs as the user types, debounced. The submit button it replaced existed for a real reason —
 * a phone keyboard's own search key, and a round trip per keystroke — and both survive: Enter still
 * submits, and the debounce is what pays for the keystrokes.
 */

import { useEffect, useMemo, useState } from "react";
import type { FormEvent, ReactElement } from "react";
import { Link } from "react-router-dom";

import { categoryChoices, copy } from "../copy.ts";
import { CaptureBar } from "../components/CaptureBar.tsx";
import { PlaceCard } from "../components/PlaceCard.tsx";
import { useToast } from "../components/Toast.tsx";
import { messageFor } from "../lib/errors.ts";
import {
  useAreas,
  useDeletePlace,
  usePlaces,
  type PlaceFilters,
} from "../lib/queries.ts";
import { useCurrentUser } from "../lib/session-context.tsx";
import { groupPlaces, groupPlacesByArea } from "../lib/placeGroups.ts";
import { deleteReport } from "./placeDeletion.ts";

/**
 * How long typing settles before the list is asked again.
 *
 * 300ms, and the number is a compromise between two costs rather than a preference: shorter and a phone
 * keyboard's key-repeat sends a query per character, longer and the list feels like it stopped listening.
 */
const SEARCH_DEBOUNCE_MS = 300;

export function PlacesScreen(): ReactElement {
  const user = useCurrentUser();
  const userId = user?.id ?? "";
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [areaId, setAreaId] = useState("");
  const [noLocation, setNoLocation] = useState(false);
  const [showQuickCapture, setShowQuickCapture] = useState(false);
  const [menuState, setMenuState] = useState<{ id: string; openUp: boolean } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  /** Which question the list is answering. Area first, because that is how a trip is planned. */
  const [view, setView] = useState<"area" | "type" | "all">("area");
  /**
   * Which sections are open, and whether the user has decided.
   *
   * A `Set` of section keys plus a flag, and the flag is the part that matters: the default below opens the
   * biggest section, and without `chosen` the user's own collapse would be undone by the next keystroke —
   * an effect that keeps re-applying a default is a control that fights the hand using it.
   */
  const [openSections, setOpenSections] = useState<ReadonlySet<string>>(new Set());
  const [sectionsChosen, setSectionsChosen] = useState(false);

  const chosenCategory = categoryChoices.find((choice) => choice.value === category)?.value;
  const filters: PlaceFilters = {
    ...(search === "" ? {} : { q: search }),
    ...(chosenCategory !== undefined ? { category: chosenCategory } : {}),
    ...(areaId === "" ? {} : { areaId }),
    ...(noLocation ? { hasLocation: false } : {}),
  };

  const places = usePlaces(userId, filters);
  const deletePlace = useDeletePlace(userId);
  const { showToast } = useToast();
  const areas = useAreas(userId);

  // Close context menu and modal on outside click or Escape key
  useEffect(() => {
    if (menuState === null && deleteTarget === null) return;
    function handleKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        setMenuState(null);
        setDeleteTarget(null);
      }
    }
    function handleClickOutside(event: MouseEvent): void {
      const target = event.target as HTMLElement | null;
      if (!target?.closest(".places-page__menu-wrap")) {
        setMenuState(null);
      }
    }
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuState, deleteTarget]);

  function onSubmit(event: FormEvent): void {
    event.preventDefault();
    setSearch(searchInput.trim());
  }

  function clearSearch(): void {
    setSearchInput("");
    setSearch("");
  }

  function clearAllFilters(): void {
    setSearchInput("");
    setSearch("");
    setCategory("");
    setAreaId("");
    setNoLocation(false);
  }

  async function onConfirmDelete(): Promise<void> {
    if (deleteTarget === null) return;
    const target = deleteTarget;
    setDeleteTarget(null);
    let response;
    try {
      response = await deletePlace.mutateAsync(target.id);
    } catch {
      return;
    }
    showToast(deleteReport(target.name, response));
  }

  /*
   * Typing settles, then the list is asked once.
   *
   * The timer is cleared on every keystroke, so a word sent letter by letter is one request rather than
   * eight — which is the cost the old submit button existed to avoid, paid for here instead of by the user.
   */
  useEffect(() => {
    const trimmed = searchInput.trim();
    if (trimmed === search) return;
    const timer = setTimeout(() => setSearch(trimmed), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, search]);

  /**
   * One row, with the actions this screen owns.
   *
   * The card is presentational and the menu, its open/closed state and the delete confirmation all live
   * here — they are this screen's, and a card that carried them would have to be told about a mutation
   * to render a button.
   */
  function renderCard(place: (typeof items)[number], showAreas: boolean): ReactElement {
    const isMenuOpen = menuState?.id === place.id;

    return (
      <PlaceCard
        key={place.id}
        place={place}
        showAreas={showAreas}
        isMenuOpen={isMenuOpen}
        actions={
          <div className="places-page__menu-wrap">
            <button
              type="button"
              className="places-page__menu-btn"
              aria-label={`Thao tác cho ${place.name}`}
              aria-expanded={isMenuOpen}
              onClick={(event) => {
                if (isMenuOpen) {
                  setMenuState(null);
                } else {
                  /*
                   * Which way the menu opens, measured against the viewport rather than guessed: the
                   * menu is 220px tall and a row near the bottom of a phone would otherwise open it off
                   * the screen.
                   */
                  const rect = event.currentTarget.getBoundingClientRect();
                  const openUp = window.innerHeight - rect.bottom < 220;
                  setMenuState({ id: place.id, openUp });
                }
              }}
            >
              ⋯
            </button>

            {isMenuOpen && (
              <div
                className={
                  menuState?.openUp
                    ? "places-page__menu-dropdown places-page__menu-dropdown--up"
                    : "places-page__menu-dropdown"
                }
                role="menu"
              >
                <Link
                  className="places-page__menu-action"
                  to={`/capture?place=${place.id}`}
                  onClick={() => setMenuState(null)}
                  role="menuitem"
                >
                  <span className="places-page__menu-icon" aria-hidden="true">✏️</span>
                  <span>{copy.places.edit}</span>
                </Link>
                <div className="places-page__menu-divider" role="separator" />
                <button
                  type="button"
                  className="places-page__menu-action places-page__menu-action--danger"
                  onClick={() => {
                    setMenuState(null);
                    setDeleteTarget({ id: place.id, name: place.name });
                  }}
                  disabled={deletePlace.isPending}
                  role="menuitem"
                >
                  <span className="places-page__menu-icon" aria-hidden="true">🗑️</span>
                  <span>{copy.places.remove}</span>
                </button>
              </div>
            )}
          </div>
        }
      />
    );
  }

  const hasQuery = search !== "";
  /*
   * Memoised on the query result: `items` feeds the grouping `useMemo` below, and rebuilt as
   * `data?.items ?? []` each render it changed identity every time, so the grouping recomputed for nothing.
   */
  const items = useMemo(() => places.data?.items ?? [], [places.data]);
  const listSettled = places.isSuccess;
  const anyFilterActive = category !== "" || areaId !== "" || noLocation;

  /*
   * The sections, from whichever grouping the switcher is on.
   *
   * Both groupings are the pure functions the rest of the app already uses, so a place cannot be a café
   * here and something else in the planner's picker. The area grouping sorts biggest first, which is what
   * makes the first section the one worth opening — the default and the order are one decision, not two.
   */
  const sections = useMemo(() => {
    if (view === "area") {
      return groupPlacesByArea(items).map((section) => ({
        key: `area:${section.key}`,
        name: section.name,
        glyph: "📍",
        count: section.places.length,
        places: section.places,
      }));
    }
    return groupPlaces(items).map((grouped) => ({
      key: `type:${grouped.group.key}`,
      name: grouped.group.label,
      glyph: grouped.group.glyph,
      count: grouped.places.length,
      places: grouped.places,
    }));
  }, [view, items]);

  useEffect(() => {
    if (sectionsChosen || sections.length === 0) return;
    setOpenSections(new Set([sections[0]!.key]));
  }, [sections, sectionsChosen]);

  function toggleSection(key: string): void {
    setSectionsChosen(true);
    setOpenSections((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <div className="screen places-page">
      <header className="places-page__header">
        <div className="places-page__title-group">
          <h1 className="places-page__title">{copy.places.heading}</h1>
          <span className="places-page__count-badge" aria-label={`${items.length} địa điểm`}>
            {items.length}
          </span>
        </div>

        <div className="places-page__header-actions">
          <button
            type="button"
            className="button button--primary places-page__action-btn"
            aria-expanded={showQuickCapture}
            onClick={() => setShowQuickCapture((open) => !open)}
          >
            {showQuickCapture ? "Đóng lưu nhanh" : "+ Thêm địa điểm"}
          </button>
          {/*
            No second way in, and the removed one is worth naming: this slot held a link to a provider
            search, and repointing it at the capture form would have put two controls with the same label
            side by side — the button opens the quick bar, and the bar's own submit already carries its
            value to the form. One control, one job.
          */}
        </div>
      </header>

      {showQuickCapture && (
        <section className="places-page__capture-drawer" aria-label={copy.captureBar.label}>
          <CaptureBar />
        </section>
      )}

      {/* Top Toolbar đa năng */}
      <section className="places-page__toolbar" aria-label={copy.places.filtersLabel}>
        <form className="places-page__search-form" onSubmit={onSubmit} role="search">
          <div className="places-page__search-wrap">
            <span className="places-page__search-icon" aria-hidden="true">
              🔍
            </span>
            <input
              className="places-page__search-input"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={copy.places.searchPlaceholder}
              aria-label={copy.places.searchLabel}
            />
            {searchInput !== "" && (
              <button
                type="button"
                className="places-page__search-clear"
                onClick={clearSearch}
                aria-label={copy.places.clearSearch}
              >
                ✕
              </button>
            )}
          </div>
        </form>

        {/*
          The category filter is a row of pills rather than a dropdown, and everything else in the bar is
          one line of compact controls under it. A dropdown for "café" hides the answer behind a tap and
          then behind a list: the whole vocabulary is ten words, and showing them makes the filter a
          question the user can read rather than one they have to open.
        */}
        <div className="places-page__pills" role="group" aria-label={copy.places.filterCategory}>
          <button
            type="button"
            className={category === "" ? "places-page__pill places-page__pill--on" : "places-page__pill"}
            aria-pressed={category === ""}
            onClick={() => setCategory("")}
          >
            {copy.places.filterAll}
          </button>
          {categoryChoices.map((choice) => (
            <button
              key={choice.value}
              type="button"
              className={
                category === choice.value ? "places-page__pill places-page__pill--on" : "places-page__pill"
              }
              aria-pressed={category === choice.value}
              onClick={() => setCategory(category === choice.value ? "" : choice.value)}
            >
              <span className="places-page__pill-glyph" aria-hidden="true">
                {choice.glyph}
              </span>
              {choice.label}
            </button>
          ))}
        </div>

        <div className="places-page__filters-bar">
          <label className="places-page__filter-group">
            <span className="places-page__filter-label">{copy.places.filterArea}</span>
            <select
              className="places-page__select"
              value={areaId}
              onChange={(event) => setAreaId(event.target.value)}
            >
              <option value="">{copy.places.filterAll}</option>
              {(areas.data?.areas ?? []).map((area) => (
                <option key={area.id} value={area.id}>
                  {area.name}
                </option>
              ))}
            </select>
          </label>

          <button
            type="button"
            className={noLocation ? "places-page__filter-chip places-page__filter-chip--on" : "places-page__filter-chip"}
            aria-pressed={noLocation}
            onClick={() => setNoLocation((on) => !on)}
          >
            {copy.places.filterNoLocation}
          </button>

          {(anyFilterActive || hasQuery) && (
            <button
              type="button"
              className="places-page__filters-clear"
              onClick={clearAllFilters}
            >
              {copy.places.filtersClear}
            </button>
          )}
        </div>

        {(anyFilterActive || hasQuery) && (
          <div className="places-page__filters-meta">
            <span className="places-page__filters-count">
              {copy.places.filteredCount(items.length)}
            </span>
            {hasQuery && (
              <span className="places-page__query-tag">
                {copy.places.searchingFor(search)}
              </span>
            )}
          </div>
        )}
      </section>

      {places.isPending && !hasQuery && <p className="state">{copy.state.loading}</p>}

      {places.isError && (
        <div className="state state--error" role="alert">
          <p>{messageFor(places.error)}</p>
          <button type="button" className="button" onClick={() => void places.refetch()}>
            {copy.state.retry}
          </button>
        </div>
      )}

      {deletePlace.isError && (
        <div className="state state--error" role="alert">
          <p>{messageFor(deletePlace.error)}</p>
        </div>
      )}

      {/* Empty states */}
      {listSettled && !hasQuery && !anyFilterActive && items.length === 0 && (
        <div className="places-page__empty-state">
          <span className="places-page__empty-icon" aria-hidden="true">📍</span>
          <p className="places-page__empty-title">{copy.places.heading}</p>
          <p className="places-page__empty-desc">{copy.places.emptyNoPlaces}</p>
          <Link className="button button--primary" to="/capture">
            {copy.capture.heading}
          </Link>
        </div>
      )}

      {listSettled && hasQuery && !anyFilterActive && items.length === 0 && (
        <div className="places-page__empty-state">
          <span className="places-page__empty-icon" aria-hidden="true">🔍</span>
          <p className="places-page__empty-title">{copy.places.searchLabel}</p>
          <p className="places-page__empty-desc">{copy.places.emptyNoMatch}</p>
          <button type="button" className="button" onClick={clearSearch}>
            {copy.places.clearSearch}
          </button>
        </div>
      )}

      {listSettled && anyFilterActive && items.length === 0 && (
        <div className="places-page__empty-state">
          <span className="places-page__empty-icon" aria-hidden="true">🏷️</span>
          <p className="places-page__empty-title">{copy.places.filtersLabel}</p>
          <p className="places-page__empty-desc">{copy.places.emptyNoMatchFiltered}</p>
          <button type="button" className="button" onClick={clearAllFilters}>
            {copy.places.filtersClear}
          </button>
        </div>
      )}

      {/*
        The switcher first, then the list in whichever shape was asked for.

        Three modes rather than a hidden preference: which question the list is answering is the first
        thing a returning user needs to see, and two taps to change one is the price of that being visible.
      */}
      {items.length > 0 && (
        <>
          <div className="segmented" role="group" aria-label={copy.places.viewLabel}>
            {(
              [
                ["area", copy.places.viewByArea],
                ["type", copy.places.viewByType],
                ["all", copy.places.viewAll],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                className={
                  view === key ? "segmented__option segmented__option--on" : "segmented__option"
                }
                aria-pressed={view === key}
                onClick={() => setView(key)}
              >
                {label}
              </button>
            ))}
          </div>

          {view === "all" ? (
            <div className="places-page__grid">{items.map((place) => renderCard(place, false))}</div>
          ) : (
            <div className="places-page__sections">
              {sections.map((section) => {
                const isOpen = openSections.has(section.key);
                const isSectionMenuOpen =
                  menuState !== null && section.places.some((place) => place.id === menuState.id);
                return (
                  <section
                    key={section.key}
                    className={
                      isSectionMenuOpen
                        ? "places-page__section places-page__section--menu-open"
                        : "places-page__section"
                    }
                  >
                    {/*
                      The whole header is the control, and its accessible name says which way the tap goes —
                      "Mở Đà Lạt" rather than "Đà Lạt", because a screen-reader user hears a button and needs
                      to know its direction. The count is in the name as well: it is the reason a folded
                      section is worth folding, and a closed section that says nothing is just a name.
                    */}
                    <button
                      type="button"
                      className="places-page__section-head"
                      aria-expanded={isOpen}
                      aria-label={
                        isOpen
                          ? copy.places.collapseSection(section.name)
                          : copy.places.expandSection(section.name)
                      }
                      onClick={() => toggleSection(section.key)}
                    >
                      <span className="places-page__section-glyph" aria-hidden="true">
                        {section.glyph}
                      </span>
                      <span className="places-page__section-name">{section.name}</span>
                      <span className="places-page__section-count">
                        {copy.places.sectionCount(section.count)}
                      </span>
                      <span className="places-page__section-chevron" aria-hidden="true">
                        ⌄
                      </span>
                    </button>
                    <div className="places-page__section-body">
                      <div className="places-page__section-rows">
                        {section.places.map((place) => renderCard(place, view !== "area"))}
                      </div>
                    </div>
                  </section>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Safe Delete Confirmation Modal */}
      {deleteTarget !== null && (
        <div
          className="places-page__modal-scrim"
          onClick={() => setDeleteTarget(null)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-delete-place-title"
        >
          <div
            className="places-page__modal"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="confirm-delete-place-title" className="places-page__modal-title">
              {copy.places.confirmRemove(deleteTarget.name)}
            </h2>
            <p className="places-page__modal-desc">
              Địa điểm này sẽ bị xoá hoàn toàn khỏi danh sách đã lưu và khỏi các lịch trình đang dùng nó.
            </p>
            <div className="places-page__modal-actions">
              <button
                type="button"
                className="button button--ghost"
                onClick={() => setDeleteTarget(null)}
              >
                {copy.capture.cancel}
              </button>
              <button
                type="button"
                className="button button--danger"
                disabled={deletePlace.isPending}
                onClick={() => void onConfirmDelete()}
              >
                {deletePlace.isPending ? "Đang xoá..." : copy.places.remove}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
